import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import SiteFooter from "@/components/SiteFooter";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KnowledgeBase = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.append("category", category);
        if (sortBy) params.append("sort", sortBy);
        params.append("limit", "100");

        const res = await fetch(`${API_URL}/api/articles?${params.toString()}`);
        const data = await res.json();
        // API returns {articles: [], total, limit, skip}
        setArticles(data.articles || []);
      } catch (err) {
        console.error("Failed to fetch articles:", err);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [category, sortBy]);

  // Filter by search term
  const filteredArticles = articles.filter(article =>
    searchTerm === "" ||
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (article.tags && article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const groupedArticles = filteredArticles.reduce((acc, a) => {
    const key = a.category || "Other";
    (acc[key] = acc[key] || []).push(a);
    return acc;
  }, {});

  const renderCard = (article) => {
    const raw = article.hero_image_url || article.heroImage || article.hero_image || "";
    const img = raw.startsWith("/") ? `${API_URL}${raw}` : raw;
    return (
      <Link
        key={article.slug}
        to={`/knowledge-base/${article.slug}`}
        className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 hover:border-teal-500 flex flex-col"
        data-testid={`kb-article-card-${article.slug}`}
      >
        {img && (
          <img
            src={img}
            alt={article.hero_image_alt || article.title}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
        )}
        <div className="p-6 flex flex-col flex-1">
          <div className="inline-block self-start px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full mb-3">
            {article.category}
          </div>
          <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition mb-3">
            {article.title}
          </h3>
          <p className="text-gray-600 text-sm mb-5 line-clamp-3 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="mt-auto flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
            <span data-testid="kb-article-meta-reads">
              {article.readMinutes} min read
              {article.reads > 0 && ` · ${article.reads.toLocaleString()} reads`}
            </span>
            <span className="inline-flex items-center gap-1 font-medium text-teal-600">
              Read guide
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <>
      <SEO
        title="Dental Knowledge Base — Expert Patient Guides | Restore Dental"
        path="/knowledge-base"
        description="Browse our comprehensive dental knowledge base with expert articles on treatments, oral health, and dental care from Dr Kartik Patel in London."
        keywords="dental knowledge base, oral health articles, dental treatment information, London dentist blog"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4">
            <Link
              to="/"
              className="inline-flex items-center text-white/90 hover:text-white mb-6 transition text-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Knowledge Base</h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl">
              Expert dental articles and guides to help you make informed decisions about your oral health.
            </p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative w-full md:w-96">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-sm"
                >
                  <option value="newest">Newest First</option>
                  <option value="reads">Most Read</option>
                  <option value="shares">Most Shared</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading articles...</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600">Try adjusting your filters or search term.</p>
            </div>
          ) : (
            <div className="space-y-16" data-testid="kb-category-sections">
              <div className="text-gray-600" data-testid="kb-results-count">
                Showing {filteredArticles.length} {filteredArticles.length === 1 ? "article" : "articles"}
                {searchTerm && <> for "<span className="font-medium text-gray-900">{searchTerm}</span>"</>}
              </div>
              {Object.keys(groupedArticles).sort().map((cat) => (
                <section key={cat} data-testid={`kb-section-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex flex-wrap items-center gap-3 mb-8 md:gap-4">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{cat}</h2>
                    <span className="text-sm font-medium text-teal-700 bg-teal-50 px-3 py-1 rounded-full">
                      {groupedArticles[cat].length} {groupedArticles[cat].length === 1 ? "article" : "articles"}
                    </span>
                    <div className="hidden sm:block flex-1 h-px bg-gradient-to-r from-teal-300 to-transparent" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {groupedArticles[cat].map((article) => renderCard(article))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </>
  );
};

export default KnowledgeBase;
